/**
 * AppSidebar.
 *
 * Left navigation, tenant-branded at the top. Desktop width is 240px expanded
 * or 64px icon-only (persisted). Mobile is a full-label slide-over.
 *
 * Only links to routes wired to real backend endpoints. When new modules
 * ship, add their nav item in sidebarNav AND the route in app/router.
 *
 * Reads manifest.modules_enabled to gate which nav items appear for
 * tenant users (e.g. People hidden if a tenant disabled the module).
 *
 * Control-plane link is gated on user.user_type === "control" — only
 * Kevin/Andrea/Natalie see it; tenant OWNERs don't.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";

import {
  ChevronLeft,
  ChevronRight,
  FileText,
  // HelpCircle, // only used by the commented-out Get Help menu below
  Landmark,
  // LogOut, // sidebar log out hidden — use top-bar AccountMenu (commented block below)
  Mail,
  PanelLeft,
  PanelLeftClose,
  RotateCcw,
} from "lucide-react";
import { Tooltip } from "antd";
import { createPortal } from "react-dom";

import { SidebarIconTooltip } from "./SidebarIconTooltip";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAccountingDashboardQuery } from "@/features/accounting/api/accountingApi";
import {
  ACCOUNTING_SECTIONS,
  buildAccountingSearchParams,
  defaultSubTab,
  getVisibleSectionIds,
  type AccountingSection,
} from "@/features/accounting/navigation/accountingNavigation";
import { useCurrentUser } from "@/features/auth/model/useAuth";
// import { useLogout } from "@/features/auth/model/useLogout"; // sidebar log out hidden
import { useRoleAccess } from "@/features/auth/model/useRoleAccess";
import { useBranding } from "@/features/branding/useBranding";
import { useDeployment } from "@/features/deployment/model/useDeployment";
import {
  grantManagementHref,
  isGrantManagementPath,
  isGrantNavLinkActive,
  isGrantPipelinePath,
} from "@/features/grants/navigation/grantManagementPaths";
import { productDisplayName } from "@/features/manifest/lib/productDisplayName";
import { useManifest } from "@/features/manifest/model/useManifest";
import { usePendingCountsQuery } from "@/features/notifications/api/pendingCountsApi";
import { useResetOnboardingMutation } from "@/features/onboarding/api/onboardingApi";

import { cn } from "@/shared/lib/cn";
import { useVerticalVocab } from "@/shared/lib/verticals";
import { TNNLogo } from "@/shared/ui/TNNLogo";

import {
  groupIdForPath,
  isParentRoute,
  NAV_GROUPS,
  UNGROUPED_ITEMS,
  type NavGroupId,
  type NavItem,
} from "./sidebarNav";
import { isNavItemVisible } from "./navVisibility";
import { useNavVisibilityCtx } from "./useNavVisibilityCtx";

function accountingHref(section: AccountingSection): string {
  const params = buildAccountingSearchParams(section.id, defaultSubTab(section));
  return `/accounting?${new URLSearchParams(params).toString()}`;
}

type GrantNavLink = {
  to: string;
  label: string;
  icon: typeof FileText;
  end: boolean;
};

type DashBadges = {
  pending_reviews?: number;
  pending_journal_approvals?: number;
  failed_syncs?: number;
};

function sectionBadge(section: AccountingSection, badges: DashBadges): number {
  if (section.badgeKey === "pending_reviews") return badges.pending_reviews ?? 0;
  if (section.badgeKey === "pending_journal_approvals")
    return badges.pending_journal_approvals ?? 0;
  if (section.badgeKey === "failed_syncs") return badges.failed_syncs ?? 0;
  return 0;
}

function isDesktopIconRail(collapsed: boolean): boolean {
  return (
    collapsed && typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
}

type AppSidebarProps = {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

/** Left navigation widget - collapsible rail, tenant-branded, role-aware. */
export function AppSidebar({
  mobileOpen,
  onMobileClose,
  collapsed,
  onToggleCollapsed,
}: AppSidebarProps) {
  const { manifest } = useManifest();
  const { logoUrl } = useBranding();
  const v = useVerticalVocab();
  const user = useCurrentUser();
  const { isClient } = useDeployment();
  const { hasMinRole, role } = useRoleAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isControl = user?.user_type === "control" && !isClient;
  const tenantName = productDisplayName(manifest);
  const { data: pending } = usePendingCountsQuery(undefined, {
    pollingInterval: 60_000,
  });

  const visibilityCtx = useNavVisibilityCtx();

  const accountingEnabled = Boolean(manifest?.modules_enabled?.accounting);
  const { data: dash } = useAccountingDashboardQuery(undefined, {
    skip: !accountingEnabled,
  });

  const visibleAccountingSections = useMemo(() => {
    const ids = getVisibleSectionIds(hasMinRole, role);
    return ACCOUNTING_SECTIONS.filter(
      (section) => section.id !== "fund_grant" && ids.includes(section.id),
    );
  }, [hasMinRole, role]);

  const grantNavLinks = useMemo(() => {
    const links: GrantNavLink[] = [];
    const pipelineOn =
      Boolean(manifest?.modules_enabled?.grant_management) &&
      visibilityCtx.grantManagementEnabled !== false;
    if (pipelineOn) {
      links.push({
        to: "/grants",
        label: "Grant pipeline",
        icon: FileText,
        end: false,
      });
    }
    if (accountingEnabled) {
      links.push({
        to: grantManagementHref("funds"),
        label: "Grant management",
        icon: Landmark,
        end: false,
      });
    }
    return links;
  }, [
    accountingEnabled,
    manifest?.modules_enabled?.grant_management,
    visibilityCtx.grantManagementEnabled,
  ]);

  const routeGroup = groupIdForPath(location.pathname);
  const [openGroups, setOpenGroups] = useState<Set<NavGroupId>>(
    () => new Set(NAV_GROUPS.map((group) => group.id)),
  );
  const prevRouteGroup = useRef(routeGroup);
  useEffect(() => {
    if (routeGroup !== prevRouteGroup.current) {
      prevRouteGroup.current = routeGroup;
      if (!routeGroup) return;
      setOpenGroups((prev) => {
        if (prev.has(routeGroup)) return prev;
        const next = new Set(prev);
        next.add(routeGroup);
        return next;
      });
    }
  }, [routeGroup]);

  const activeAccountingSection = searchParams.get("section") ?? "dashboard";
  const inAccountingArea = location.pathname.startsWith("/accounting");
  const accountingNavActive = inAccountingArea;
  const onGrantManagementArea = isGrantManagementPath(location.pathname);
  const onGrantsPipeline = isGrantPipelinePath(location.pathname);
  const onGrantsArea = onGrantsPipeline || onGrantManagementArea;

  const [accountingSubnavOpen, setAccountingSubnavOpen] = useState(false);
  /** User tapped Back on accounting child nav while staying on /accounting. */
  const userDismissedAccountingSubnavRef = useRef(false);

  const [grantsOpen, setGrantsOpen] = useState(onGrantsArea);
  useEffect(() => {
    if (onGrantsArea) setGrantsOpen(true);
  }, [onGrantsArea]);

  // Accounting-only child sidebar (Back). Does not drive Grants or other inline nests.
  useEffect(() => {
    if (isDesktopIconRail(collapsed)) {
      setAccountingSubnavOpen(false);
      return;
    }

    const path = location.pathname;
    if (path.startsWith("/accounting")) {
      if (!userDismissedAccountingSubnavRef.current) setAccountingSubnavOpen(true);
      return;
    }

    userDismissedAccountingSubnavRef.current = false;
    setAccountingSubnavOpen(false);
  }, [location.pathname, collapsed]);

  const openAccountingSubnav = () => {
    userDismissedAccountingSubnavRef.current = false;
    setAccountingSubnavOpen(true);
  };

  const closeAccountingSubnav = () => {
    userDismissedAccountingSubnavRef.current = true;
    setAccountingSubnavOpen(false);
  };

  const showAccountingSubnavPanel =
    !isDesktopIconRail(collapsed) && accountingSubnavOpen && accountingNavActive;

  const isAccountingSectionActive = (sectionId: string) =>
    inAccountingArea && activeAccountingSection === sectionId;

  const [accountingFlyout, setAccountingFlyout] = useState(false);
  const accountingButtonRef = useRef<HTMLButtonElement>(null);
  const [grantsFlyout, setGrantsFlyout] = useState(false);
  const grantsButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!collapsed) {
      setAccountingFlyout(false);
      setGrantsFlyout(false);
    }
  }, [collapsed]);

  const [resetOnboarding, { isLoading: restartingWalkthrough }] = useResetOnboardingMutation();
  const handleRestartWalkthrough = () => {
    void resetOnboarding()
      .unwrap()
      .then(() => navigate("/"));
  };

  const toggleGroup = (id: NavGroupId) => {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dashBadges: DashBadges = {
    pending_reviews: dash?.pending_reviews,
    pending_journal_approvals: dash?.pending_journal_approvals,
    failed_syncs: dash?.failed_syncs,
  };

  const renderLink = (item: NavItem) => {
    const label = item.vocabKey ? v.nav[item.vocabKey] || item.label : item.label;
    const badge = item.badgeKey ? (pending?.[item.badgeKey] ?? 0) : 0;

    if (item.nestedAccounting) {
      return (
        <AccountingNavRow
          key={item.to}
          item={item}
          label={label}
          collapsed={collapsed}
          active={accountingNavActive}
          onOpen={() => {
            if (isDesktopIconRail(collapsed)) {
              setAccountingFlyout((open) => !open);
              return;
            }
            if (!accountingNavActive) {
              const dashboard = ACCOUNTING_SECTIONS.find((s) => s.id === "dashboard");
              if (dashboard) navigate(accountingHref(dashboard));
            }
            openAccountingSubnav();
          }}
          buttonRef={accountingButtonRef}
        />
      );
    }

    if (item.nestedGrants) {
      return (
        <GrantsNavRow
          key={item.to}
          item={item}
          label={label}
          collapsed={collapsed}
          open={grantsOpen}
          active={onGrantsArea}
          onToggle={() => {
            if (isDesktopIconRail(collapsed)) {
              setGrantsFlyout((open) => !open);
              return;
            }
            setGrantsOpen((open) => !open);
          }}
          buttonRef={grantsButtonRef}
          links={grantNavLinks}
          onNavigate={onMobileClose}
        />
      );
    }

    const iconRail = isDesktopIconRail(collapsed);

    return (
      <li key={item.to} className="relative">
        <SidebarIconTooltip label={label} enabled={iconRail}>
          <NavLink
            to={item.to}
            end={isParentRoute(item.to)}
            onClick={onMobileClose}
            className={({ isActive }) =>
              cn(
                "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                collapsed && "lg:justify-center lg:px-2",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50",
              )
            }
          >
            <item.icon className="size-4 shrink-0" />
            <span className={cn("flex-1 truncate", collapsed && "lg:hidden")}>{label}</span>
            {badge > 0 && (
              <span
                className={cn(
                  "min-w-5 rounded-full bg-primary px-1.5 py-0.5 text-center text-3xs font-bold tabular-nums text-primary-foreground",
                  collapsed && "lg:absolute lg:right-1 lg:top-1 lg:min-w-4 lg:px-1 lg:text-[9px]",
                )}
                aria-label={`${badge} waiting`}
              >
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </NavLink>
        </SidebarIconTooltip>
      </li>
    );
  };

  return (
    <aside
      aria-expanded={!collapsed}
      className={cn(
        "fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-[width,transform] duration-200",
        "w-60 lg:translate-x-0",
        collapsed ? "lg:w-16" : "lg:w-60",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
      )}
    >
      <div className={cn("border-b border-sidebar-border py-3", collapsed ? "lg:px-2" : "px-3")}>
        <div className={cn("flex items-center gap-3", collapsed && "lg:justify-center lg:gap-0")}>
          {logoUrl ? (
            <img src={logoUrl} alt="" className="size-8 shrink-0 rounded-md object-contain" />
          ) : (
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-sidebar-accent-foreground">
              <TNNLogo variant="mark" className="size-6" />
            </div>
          )}
          <div className={cn("min-w-0 flex-1", collapsed && "lg:hidden")}>
            <div className="truncate text-sm font-medium text-sidebar-foreground">{tenantName}</div>
            {isControl && <div className="truncate text-xs text-muted-foreground">TNN Admin</div>}
          </div>
          {isDesktopIconRail(collapsed) ? (
            <Tooltip title="Expand sidebar" placement="right" mouseEnterDelay={0.15}>
              <button
                type="button"
                onClick={onToggleCollapsed}
                className="hidden shrink-0 cursor-pointer rounded-md p-1.5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground lg:inline-flex"
                aria-label="Expand sidebar"
              >
                <PanelLeft className="size-4" aria-hidden />
              </button>
            </Tooltip>
          ) : (
            <Tooltip title="Collapse sidebar" placement="bottom" mouseEnterDelay={0.15}>
              <button
                type="button"
                onClick={onToggleCollapsed}
                className="hidden shrink-0 cursor-pointer rounded-md p-1.5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground lg:inline-flex"
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose className="size-4" aria-hidden />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <nav className="scrollbar-standard flex-1 overflow-y-auto p-2" aria-label="Main">
        {showAccountingSubnavPanel ? (
          <AccountingSubnavPanel onBack={closeAccountingSubnav}>
            <ul className="space-y-0.5">
              {visibleAccountingSections.map((section) => (
                <li key={section.id}>
                  <AccountingSectionLink
                    section={section}
                    active={isAccountingSectionActive(section.id)}
                    badge={sectionBadge(section, dashBadges)}
                    onNavigate={onMobileClose}
                  />
                </li>
              ))}
            </ul>
          </AccountingSubnavPanel>
        ) : (
          <ul className="space-y-0.5">
            {UNGROUPED_ITEMS.filter((item) => isNavItemVisible(item, visibilityCtx)).map(
              renderLink,
            )}

            {NAV_GROUPS.map((group) => {
              const visibleItems = group.items.filter((item) =>
                isNavItemVisible(item, visibilityCtx),
              );
              if (visibleItems.length === 0) return null;
              const groupOpen = openGroups.has(group.id);
              return (
                <li key={group.id} className="relative">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className={cn(
                      "mt-3 flex w-full items-center gap-1 border-t border-sidebar-border px-3 pt-3 text-left text-3xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-sidebar-foreground",
                      collapsed && "lg:hidden",
                    )}
                    aria-expanded={groupOpen}
                  >
                    <ChevronRight
                      className={cn(
                        "size-3 shrink-0 transition-transform",
                        groupOpen && "rotate-90",
                      )}
                    />
                    {group.label}
                  </button>
                  {collapsed && (
                    <div
                      className="mt-2 hidden border-t border-sidebar-border lg:block"
                      aria-hidden
                    />
                  )}
                  <ul
                    className={cn("space-y-0.5", !groupOpen && "hidden", collapsed && "lg:block")}
                  >
                    {visibleItems.map(renderLink)}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {collapsed && accountingFlyout && (
        <AccountingSectionsFlyout
          anchorRef={accountingButtonRef}
          sections={visibleAccountingSections}
          activeSectionId={activeAccountingSection}
          dashBadges={dashBadges}
          onClose={() => setAccountingFlyout(false)}
          onNavigate={() => {
            setAccountingFlyout(false);
            onMobileClose?.();
          }}
        />
      )}

      {collapsed && grantsFlyout && (
        <GrantsNavFlyout
          anchorRef={grantsButtonRef}
          links={grantNavLinks}
          onClose={() => setGrantsFlyout(false)}
          onNavigate={() => {
            setGrantsFlyout(false);
            onMobileClose?.();
          }}
        />
      )}

      <div className={cn("border-t border-sidebar-border py-3", collapsed ? "lg:px-1" : "px-3")}>
        {/* Get Help — commented out (not removed) on request; uncomment to restore. Contact Support remains.
        {!inAccountingArea ? (
          <a
            href="mailto:support@new901.io?subject=TNN%20Support%20Request"
            title="Get Help"
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent/50",
              collapsed && "lg:justify-center lg:px-2",
            )}
          >
            <HelpCircle className="size-4 shrink-0" />
            <span className={cn(collapsed && "lg:hidden")}>Get Help</span>
          </a>
        ) : null}
        */}
        <SidebarIconTooltip label="Contact Support" enabled={isDesktopIconRail(collapsed)}>
          <a
            href="mailto:support@new901.io?subject=TNN%20Support%20Request"
            className={cn(
              "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent/50",
              collapsed && "lg:justify-center lg:px-2",
            )}
          >
            <Mail className="size-4 shrink-0" />
            <span className={cn(collapsed && "lg:hidden")}>Contact Support</span>
          </a>
        </SidebarIconTooltip>
        <SidebarIconTooltip label="Restart walkthrough" enabled={isDesktopIconRail(collapsed)}>
          <button
            type="button"
            onClick={handleRestartWalkthrough}
            disabled={restartingWalkthrough}
            className={cn(
              "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent/50 disabled:opacity-60",
              collapsed && "lg:justify-center lg:px-2",
            )}
          >
            <RotateCcw className="size-4 shrink-0" />
            <span className={cn(collapsed && "lg:hidden")}>Restart walkthrough</span>
          </button>
        </SidebarIconTooltip>
      </div>

      {/*
        Profile + log out live in the top-bar AccountMenu (not duplicated here).
        Sidebar footer block (commented, not deleted):
        {user && (
          <div className="border-t border-sidebar-border py-3 …">
            name, role badge, email, Log out button
          </div>
        )}
      */}
    </aside>
  );
}

/** Full-width accounting section list with Back — not used for Grants (inline only). */
function AccountingSubnavPanel({ onBack, children }: { onBack: () => void; children: ReactNode }) {
  return (
    <div className="flex min-h-0 flex-col">
      <button
        type="button"
        onClick={onBack}
        className="mb-2 flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent/50"
      >
        <ChevronLeft className="size-4 shrink-0" aria-hidden />
        Back
      </button>
      <div className="mb-2 px-2 text-sm font-semibold text-sidebar-foreground">Accounting</div>
      {children}
    </div>
  );
}

function GrantsNavRow({
  item,
  label,
  collapsed,
  open,
  active,
  onToggle,
  buttonRef,
  links,
  onNavigate,
}: {
  item: NavItem;
  label: string;
  collapsed: boolean;
  open: boolean;
  active: boolean;
  onToggle: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
  links: GrantNavLink[];
  onNavigate?: () => void;
}) {
  const iconRail = isDesktopIconRail(collapsed);

  return (
    <li className="relative">
      <SidebarIconTooltip label={label} enabled={iconRail}>
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          data-nav-active={active ? "true" : "false"}
          onClick={onToggle}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            collapsed && "lg:justify-center lg:px-2",
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent/50",
          )}
        >
          <item.icon className="size-4 shrink-0" />
          <span className={cn("flex-1 truncate text-left", collapsed && "lg:hidden")}>{label}</span>
          <ChevronRight
            className={cn(
              "size-3.5 shrink-0 transition-transform",
              collapsed && "lg:hidden",
              open && "rotate-90",
            )}
          />
        </button>
      </SidebarIconTooltip>
      {open && (
        <ul
          className={cn(
            "mt-0.5 ml-5 space-y-0.5 border-l border-sidebar-border pl-1",
            collapsed && "lg:hidden",
          )}
        >
          {links.map((link) => (
            <li key={link.to}>
              <GrantNavChildLink link={link} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function GrantNavChildLink({ link, onNavigate }: { link: GrantNavLink; onNavigate?: () => void }) {
  const Icon = link.icon;
  const location = useLocation();
  const navActive = isGrantNavLinkActive(link.to, location.pathname);

  return (
    <NavLink
      to={link.to}
      end={link.end}
      onClick={onNavigate}
      aria-current={navActive ? "page" : undefined}
      data-nav-active={navActive ? "true" : "false"}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
        navActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
      )}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-80" />
      <span className="min-w-0 flex-1 truncate">{link.label}</span>
    </NavLink>
  );
}

function GrantsNavFlyout({
  anchorRef,
  links,
  onClose,
  onNavigate,
}: {
  anchorRef: RefObject<HTMLButtonElement | null>;
  links: GrantNavLink[];
  onClose: () => void;
  onNavigate: () => void;
}) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.right + 8 });
  }, [anchorRef]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      const flyout = document.getElementById("grants-nav-flyout");
      if (flyout?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div
      id="grants-nav-flyout"
      role="menu"
      aria-label="Grants sections"
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-60 max-h-[min(28rem,calc(100vh-2rem))] w-56 overflow-y-auto scrollbar-standard rounded-md border border-sidebar-border bg-sidebar p-1 shadow-lg"
    >
      {links.map((link) => (
        <GrantNavChildLink key={link.to} link={link} onNavigate={onNavigate} />
      ))}
    </div>,
    document.body,
  );
}

function AccountingNavRow({
  item,
  label,
  collapsed,
  active,
  onOpen,
  buttonRef,
}: {
  item: NavItem;
  label: string;
  collapsed: boolean;
  active: boolean;
  onOpen: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
}) {
  const iconRail = isDesktopIconRail(collapsed);

  return (
    <li className="relative">
      <SidebarIconTooltip label={label} enabled={iconRail}>
        <button
          ref={buttonRef}
          type="button"
          onClick={onOpen}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            collapsed && "lg:justify-center lg:px-2",
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent/50",
          )}
        >
          <item.icon className="size-4 shrink-0" />
          <span className={cn("flex-1 truncate text-left", collapsed && "lg:hidden")}>{label}</span>
          <ChevronRight className={cn("size-3.5 shrink-0 opacity-70", collapsed && "lg:hidden")} />
        </button>
      </SidebarIconTooltip>
    </li>
  );
}

function AccountingSectionLink({
  section,
  active,
  badge,
  onNavigate,
}: {
  section: AccountingSection;
  active: boolean;
  badge: number;
  onNavigate?: () => void;
}) {
  const Icon = section.icon;
  return (
    <NavLink
      to={accountingHref(section)}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
      )}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-80" />
      <span className="min-w-0 flex-1 truncate">{section.label}</span>
      {badge > 0 && (
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
            section.badgeKey === "failed_syncs"
              ? "bg-red-100 text-red-700"
              : "bg-amber-100 text-amber-800",
          )}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

function AccountingSectionsFlyout({
  anchorRef,
  sections,
  activeSectionId,
  dashBadges,
  onClose,
  onNavigate,
}: {
  anchorRef: RefObject<HTMLButtonElement | null>;
  sections: AccountingSection[];
  activeSectionId: string;
  dashBadges: DashBadges;
  onClose: () => void;
  onNavigate: () => void;
}) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.right + 8 });
  }, [anchorRef]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      const flyout = document.getElementById("accounting-nav-flyout");
      if (flyout?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div
      id="accounting-nav-flyout"
      role="menu"
      aria-label="Accounting sections"
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-60 max-h-[min(28rem,calc(100vh-2rem))] w-56 overflow-y-auto scrollbar-standard rounded-md border border-sidebar-border bg-sidebar p-1 shadow-lg"
    >
      {sections.map((section) => (
        <AccountingSectionLink
          key={section.id}
          section={section}
          active={activeSectionId === section.id}
          badge={sectionBadge(section, dashBadges)}
          onNavigate={onNavigate}
        />
      ))}
    </div>,
    document.body,
  );
}
